<template>
  <modal
    :header="true"
    :footer="true"
    transition-name="modal-form__slide"
    :close-button="true"
    disable-click-outside
    class="experience-modal"
    data-test="graphics-experienceContentModal"
    auto-height
    @close="onCancel"
  >
    <template #modal-header>
      <h2>Setup Experience</h2>
    </template>

    <template #modal-body>
      <!-- Loading State -->
      <div
        v-if="loading"
        class="experience-modal__loading"
      >
        <span class="fas fa-spinner fa-spin" />
        <p>Loading experiences...</p>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="experience-modal__error"
      >
        <span class="fas fa-exclamation-triangle" />
        <p>{{ error }}</p>
      </div>

      <!-- Loaded State -->
      <template v-else>
        <p class="experience-modal__description">
          Select the Experience you want to display:
        </p>

        <!-- Experience Picker -->
        <select
          v-model="selectedExperienceId"
          class="width-full"
          data-test="header-select-account"
        >
          <option
            v-for="e in experiences"
            :key="e.id"
            :value="e.id"
          >
            {{ e.name }}
          </option>
        </select>
        <!-- End Experience Picker -->

        <!-- Dynamic form based on chosen Experience -->
        <hr v-if="formFields.length">

        <h5 v-if="formFields.length">
          Additional Fields
        </h5>

        <div
          v-for="field in formFields"
          :key="field.key"
          class="experience-field"
        >
          <label>
            {{ field.label }}

            <input
              v-if="field.isText"
              v-model="fieldValues[field.key]"
              :placeholder="field.defaultValue || 'Value'"
            />

            <select
              v-else-if="field.options?.length"
              v-model="fieldValues[field.key]"
            >
              <option
                v-for="option in field.options"
                :key="option.id"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>
        <!-- End Dynamic form based on chosen Experience -->
      </template>
    </template>

    <!-- Error State Footer -->
    <template
      v-if="error" #modal-footer
    >
      <div class="display-flex items-center gap-1 flex-end">
        <button
          class="btn btn-style-b no-tooltip"
          @click="loadExperiences"
        >
          Retry
        </button>

        <button
          v-if="errorStatus === 401"
          class="btn btn-style-a no-tooltip"
          @click="reconnect"
        >
          Reconnect Account
        </button>
      </div>
    </template>

    <!-- Multiple submit buttons if Experience has both a URL _and_ qrCodeUrl -->
    <template
      v-else-if="url && qrCodeUrl" #modal-footer
    >
      <div class="display-flex items-center gap-1 flex-end">
        <button
          class="btn btn-style-b no-tooltip"
          :data-test="`graphics-addExperienceTemplate`"
          :disabled="!isFormValid || loading"
          :data-entity="`graphics-addExperienceTemplate`"
          @click="sendSelections({ frame: false })"
        >
          QR Code Only
        </button>

        <button
          class="btn btn-style-b no-tooltip"
          :data-test="`graphics-addExperienceTemplate`"
          :disabled="!isFormValid || loading"
          :data-entity="`graphics-addExperienceTemplate`"
          @click="sendSelections({ qrCode: false })"
        >
          Display Only
        </button>

        <button
          class="btn btn-style-a no-tooltip experience-modal__primary-action"
          :data-test="`graphics-addExperienceTemplate`"
          :disabled="!isFormValid || loading"
          :data-entity="`graphics-addExperienceTemplate`"
          @click="sendSelections"
        >
          Add Both
        </button>
      </div>
    </template>

    <!-- One submit button if Experience only has a URL _or_ qrCodeUrl -->
    <template
      v-else #modal-footer
    >
      <div class="display-flex items-center gap-1 flex-end">
        <button
          class="btn btn-style-a no-tooltip"
          :data-test="`graphics-addExperienceTemplate`"
          :disabled="!isFormValid || loading"
          :data-entity="`graphics-addExperienceTemplate`"
          @click="sendSelections"
        >
          Add {{ url ? 'View' : 'QR Code' }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from './Modal.vue';
import Partners from '../../services/partners';

export default {
  emits: ['reconnect', 'close', 'select'],
  components: {
    Modal,
  },

  props: {
    partner: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      experiences: [],
      selectedExperienceId: null,
      fieldValues: {},
      loading: true,
      error: null,
      errorStatus: null,
    };
  },

  computed: {
    experience() {
      return (
        this.selectedExperienceId
        && this.experiences.find((e) => e.id === this.selectedExperienceId)
      );
    },

    url() {
      return this.experience?.url;
    },

    qrCodeUrl() {
      return this.experience?.qrCodeUrl;
    },

    formFields() {
      return this.experience?.fields ?? [];
    },

    isFormValid() {
      if (!this.selectedExperienceId) {
        return false;
      }

      // Check if all form fields have values
      return this.formFields.every((field) => {
        const value = this.fieldValues[field.key];
        // Allow empty string for dropdowns, but not for text fields
        if (field.isText) {
          return value !== '' && value !== null && value !== undefined;
        }
        return value !== null && value !== undefined;
      });
    },
  },

  watch: {
    selectedExperienceId() {
      // Initialize any needed fields on selected experience.
      if (this.experience) {
        this.experience.fields?.forEach((field) => {
          if (field.isText) {
            // Switch to default value, or leave existing text if key matches another Experience.
            this.fieldValues[field.key] = field.defaultValue ?? this.fieldValues[field.key] ?? '';
          } else {
            // Default to first option in list if default value not provided.
            this.fieldValues[field.key] = field.defaultValue ?? field.options?.[0]?.value ?? null;
          }
        });
      }
    },
  },

  mounted() {
    this.loadExperiences();
  },

  methods: {
    async loadExperiences() {
      this.loading = true;
      this.error = null;
      this.errorStatus = null;

      try {
        const resp = await Partners.getExperiences(this.partner);

        if (resp.error) {
          this.errorStatus = resp.status || null;
          this.error = resp.error;
        } else {
          this.experiences = resp.experiences ?? [];
          this.selectedExperienceId = this.experiences?.[0]?.id || null;
        }
      } catch (err) {
        this.errorStatus = err.status || null;
        this.error = err.message || 'Failed to load experiences. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    reconnect() {
      this.$emit('reconnect', this.partner);
    },

    /**
     * Construct URL to be used by browser source elements,
     * using any provided fields and replacing template strings in
     * the provided Experience URL.
     */
    constructUrl(url) {
      if (!url) { return null; }

      let out = url;

      // Loop over fields defined on Experience,
      // and replace any template strings in the URL.
      this.experience.fields?.forEach((field) => {
        out = out.replace(
          new RegExp(`{{${field.key}}}`, 'g'),
          encodeURIComponent(this.fieldValues[field.key]),
        );
      });

      // Replace {{experience_id}}
      out = out.replace(/{{experience_id}}/g, this.experience.id);
      return out;
    },

    onCancel() {
      this.$emit('close');
    },

    sendSelections(opts = {}) {
      const {
        frame = true,
        qrCode = true,
      } = opts;

      const url = this.constructUrl(this.url);
      const qrCodeUrl = this.constructUrl(this.qrCodeUrl);

      if (!url && !qrCodeUrl) {
        this.$emit('close');
        return;
      }

      this.$emit('select', {
        experienceId: this.experience.id,
        name: this.experience.name,
        url: frame ? url : null,
        qrCodeUrl: qrCode ? qrCodeUrl : null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.experience-field label {
  display: flex;
  flex-direction: column;
}

.experience-modal__loading,
.experience-modal__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;

  .fa-spinner,
  .fa-exclamation-triangle {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  .fa-spinner {
    color: #00b4cc;
  }

  .fa-exclamation-triangle {
    color: #f59e0b;
  }

  p {
    margin: 0;
    color: #64748b;
  }
}

.experience-modal__primary-action {
  box-shadow: 0 2px 6px rgba(0, 127, 157, 0.2);
}
</style>
